import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Plus, Database, Loader2, Trash2, Plug, Search, Shield, Cloud, Server, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { getDBConnections, createDBConnection, deleteDBConnection, testDBConnection, scanDBConnection, checkDBCompliance } from '@/services/dpdpa';

const CLOUD_PROVIDERS = [
  { value: 'on_prem', label: 'On-Premises', icon: Server },
  { value: 'aws_rds', label: 'AWS RDS', icon: Cloud },
  { value: 'azure', label: 'Azure', icon: Cloud },
  { value: 'gcp_cloudsql', label: 'GCP Cloud SQL', icon: Cloud },
  { value: 'aws_documentdb', label: 'AWS DocumentDB', icon: Cloud },
  { value: 'azure_cosmos', label: 'Azure Cosmos DB', icon: Cloud },
];

const statusBadge = (status) => {
  const map = { configured: 'bg-gray-500/10 text-gray-600', connected: 'bg-emerald-500/10 text-emerald-700', scanned: 'bg-blue-500/10 text-blue-700', error: 'bg-red-500/10 text-red-700' };
  return map[status] || map.configured;
};

export default function DBConnectionsPage() {
  const [connections, setConnections] = useState([]);
  const [dbTypes, setDbTypes] = useState({});
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);
  const [scanning, setScanning] = useState(null);
  const [checking, setChecking] = useState(null);
  const [form, setForm] = useState({ name: '', db_type: 'postgresql', cloud_provider: 'on_prem', host: '', port: 5432, username: '', password: '', database: '', ssl: false });

  const fetchConnections = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getDBConnections();
      setConnections(res.connections || []);
      setDbTypes(res.db_types || {});
    } catch (err) {
      toast.error('Failed to load connections');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchConnections(); }, [fetchConnections]);

  const handleCreate = async () => {
    if (!form.name || !form.host || !form.database) { toast.error('Name, Host, and Database are required'); return; }
    try {
      setSaving(true);
      await createDBConnection(form);
      toast.success('Connection added');
      setShowDialog(false);
      setForm({ name: '', db_type: 'postgresql', cloud_provider: 'on_prem', host: '', port: 5432, username: '', password: '', database: '', ssl: false });
      fetchConnections();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create connection');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async (id) => {
    try {
      setTesting(id);
      const result = await testDBConnection(id);
      if (result.success) toast.success(result.message);
      else toast.error(result.message);
      fetchConnections();
    } catch (err) {
      toast.error('Connection test failed');
    } finally {
      setTesting(null);
    }
  };

  const handleScan = async (id) => {
    try {
      setScanning(id);
      toast.info('Scanning database for PII...');
      const result = await scanDBConnection(id);
      toast.success(`Scan complete: ${result.scan_summary?.total_pii_fields || 0} PII fields found`);
      fetchConnections();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Scan failed');
    } finally {
      setScanning(null);
    }
  };

  const handleComplianceCheck = async (id) => {
    try {
      setChecking(id);
      const result = await checkDBCompliance(id);
      const status = result.compliance_status;
      if (status === 'compliant') toast.success('Database is DPDPA compliant!');
      else toast.warning(`Status: ${status.replace(/_/g, ' ')} — ${result.total_issues} issues found (${result.critical_issues} critical)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Compliance check failed');
    } finally {
      setChecking(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this connection?')) return;
    try {
      await deleteDBConnection(id);
      toast.success('Connection deleted');
      fetchConnections();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleDbTypeChange = (v) => {
    const defaultPort = dbTypes[v]?.default_port || 5432;
    setForm(prev => ({ ...prev, db_type: v, port: defaultPort }));
  };

  if (loading) return <div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin opacity-50" /></div>;

  return (
    <div className="p-6 space-y-6" data-testid="db-connections-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Database Connections</h1>
          <p className="text-sm opacity-60">Connect to external databases for PII scanning and DPDPA compliance checks</p>
        </div>
        <Button onClick={() => setShowDialog(true)} data-testid="add-db-connection-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Connection
        </Button>
      </div>

      {/* Supported DB Types */}
      <div className="flex flex-wrap gap-2">
        {Object.entries(dbTypes).map(([key, info]) => (
          <Badge key={key} variant="outline" className="text-xs py-1 px-3">{info.label}</Badge>
        ))}
      </div>

      {connections.length === 0 ? (
        <Card><CardContent className="p-8 text-center opacity-50"><Database className="w-10 h-10 mx-auto mb-2 opacity-30" /><p>No database connections configured. Add a connection to start scanning.</p></CardContent></Card>
      ) : (
        <div className="grid gap-3">
          {connections.map((conn) => (
            <Card key={conn.id} data-testid={`db-conn-${conn.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Database className="w-4 h-4 opacity-50" />
                      <h3 className="font-semibold">{conn.name}</h3>
                      <Badge variant="outline" className="text-xs">{dbTypes[conn.db_type]?.label || conn.db_type}</Badge>
                      <Badge variant="outline" className="text-xs capitalize">{conn.cloud_provider.replace(/_/g, ' ')}</Badge>
                      <Badge className={`text-xs ${statusBadge(conn.status)}`}>{conn.status}</Badge>
                    </div>
                    <div className="text-xs opacity-50 mt-1">
                      <span>{conn.host}:{conn.port}/{conn.database}</span>
                      {conn.ssl && <Badge variant="outline" className="text-xs ml-2">SSL</Badge>}
                      {conn.last_scanned_at && <span className="ml-3">Last scan: {new Date(conn.last_scanned_at).toLocaleString()}</span>}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="outline" size="sm" onClick={() => handleTest(conn.id)} disabled={testing === conn.id} data-testid={`test-conn-${conn.id}`}>
                      {testing === conn.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plug className="w-3 h-3" />}
                      <span className="ml-1">Test</span>
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleScan(conn.id)} disabled={scanning === conn.id} data-testid={`scan-conn-${conn.id}`}>
                      {scanning === conn.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}
                      <span className="ml-1">Scan PII</span>
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleComplianceCheck(conn.id)} disabled={checking === conn.id} data-testid={`check-conn-${conn.id}`}>
                      {checking === conn.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Shield className="w-3 h-3" />}
                      <span className="ml-1">Check</span>
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(conn.id)} data-testid={`delete-conn-${conn.id}`}>
                      <Trash2 className="w-4 h-4 text-red-400" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add Connection Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg" data-testid="add-db-dialog">
          <DialogHeader><DialogTitle>Add Database Connection</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Connection Name</Label>
              <Input value={form.name} onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))} placeholder="e.g. Production PostgreSQL" data-testid="db-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Database Type</Label>
                <Select value={form.db_type} onValueChange={handleDbTypeChange}>
                  <SelectTrigger data-testid="db-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(dbTypes).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Cloud Provider</Label>
                <Select value={form.cloud_provider} onValueChange={v => setForm(prev => ({ ...prev, cloud_provider: v }))}>
                  <SelectTrigger data-testid="db-cloud-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CLOUD_PROVIDERS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <Label>Host</Label>
                <Input value={form.host} onChange={e => setForm(prev => ({ ...prev, host: e.target.value }))} placeholder="e.g. db.example.com" data-testid="db-host-input" />
              </div>
              <div>
                <Label>Port</Label>
                <Input type="number" value={form.port} onChange={e => setForm(prev => ({ ...prev, port: parseInt(e.target.value) || 5432 }))} data-testid="db-port-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Username</Label>
                <Input value={form.username} onChange={e => setForm(prev => ({ ...prev, username: e.target.value }))} data-testid="db-user-input" />
              </div>
              <div>
                <Label>Password</Label>
                <Input type="password" value={form.password} onChange={e => setForm(prev => ({ ...prev, password: e.target.value }))} data-testid="db-pass-input" />
              </div>
            </div>
            <div>
              <Label>Database Name</Label>
              <Input value={form.database} onChange={e => setForm(prev => ({ ...prev, database: e.target.value }))} placeholder="e.g. production_db" data-testid="db-database-input" />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.ssl} onCheckedChange={v => setForm(prev => ({ ...prev, ssl: v }))} data-testid="db-ssl-switch" />
              <Label>Use SSL/TLS</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={saving} data-testid="save-db-conn-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Add Connection
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
